import React, { useEffect, useImperativeHandle, useState } from "react";
import styled from "@emotion/styled";
import { Box, IconButton, Typography } from "@mui/material";
import dayjs from "dayjs";
import {
  DefaultDateInput,
  DefaultInputSmall,
} from "src/components";
import { PlusIcon } from "src/Icons";
import { TrashBinMenuIconFilled } from "src/Icons/TrashBinMenuIconFilled";

import { SectionHeader } from ".";

const DeleteButton = styled(IconButton)({
  marginLeft: "8px",
  color: "#B9B9B9",
  "&:hover, &:active": {
    backgroundColor: "transparent",
  },
});

interface DayOffRulesSectionProps {
  period: any;
  daysOffReason: string;
  onChange: (newDayOffs: any, newReason: string) => any;
  onDayDeletion: React.Dispatch<any>;
  daysOffInputs: any;
  setDaysOffInputs: React.Dispatch<any>;
}

export const DayOffRulesSection = React.forwardRef(
  ({
    period,
    daysOffReason,
    onChange,
    daysOffInputs,
    setDaysOffInputs,
    onDayDeletion,
  }: DayOffRulesSectionProps, ref) => {
    const [reason, setReason] = useState(daysOffReason);

    useEffect(() => {
      onChange && onChange(daysOffInputs, reason);
    }, []);

    const handleAddMore = () => {
      setDaysOffInputs((prevInputs: any) => {
        const newInputs = [
          ...prevInputs,
          {
            id: prevInputs.length + 1,
            label: `Day off date ${prevInputs.length + 1}`,
            value: "",
          },
        ];
        onChange(newInputs, reason);
        return newInputs;
      });
    };

    useImperativeHandle(ref, () => ({
      getDaysOff: () => {
        const newDayOffs = daysOffInputs.map(({ value }: any, id: any) => {
          const timeStr =
            typeof value === "string" ? value : value.toISOString();

          return {
            id,
            period: {
              from: timeStr,
              to: timeStr,
            },
            reason,
          };
        });

        return newDayOffs;
      },
    }), [daysOffInputs, reason]);

    const setСorrectDate = (
      index: number,
      newValue: any,
      id: any,
      label: any
    ) => {

      if (
        dayjs(period?.to).format("DD.MM.YY") ===
        dayjs(newValue).format("DD.MM.YY")
      ) {
        const newArray = [
          ...daysOffInputs.slice(0, index),
          {
            id,
            label,
            value: newValue,
          },
          ...daysOffInputs.slice(index + 1),
        ];
        onChange(newArray, reason)
        setDaysOffInputs(newArray);
      } else if (dayjs(period?.from) > newValue) {
        const newArray = [
          ...daysOffInputs.slice(0, index),
          {
            id,
            label,
            value: dayjs(period?.from),
          },
          ...daysOffInputs.slice(index + 1),
        ];
        onChange(newArray, reason)
        setDaysOffInputs(newArray);
      } else if (dayjs(period?.to) < newValue) {
        const newArray = [
          ...daysOffInputs.slice(0, index),
          {
            id,
            label,
            value: dayjs(period?.to),
          },
          ...daysOffInputs.slice(index + 1),
        ];
        onChange(newArray, reason)
        setDaysOffInputs(newArray);
      } else {
        const newArray = [
          ...daysOffInputs.slice(0, index),
          {
            id,
            label,
            value: newValue,
          },
          ...daysOffInputs.slice(index + 1),
        ];
        onChange(newArray, reason)
        setDaysOffInputs(newArray);
      }
    };

    const handleDeleteDaysOff = (
      id: number,
      value: string,
    ) => {
      setDaysOffInputs((prevDaysOffInputs: any) => {
        const updatedDaysOffInputs = prevDaysOffInputs.filter((dayOff: any) => dayOff.id !== id);

        const updatedDaysOffInputsWithLabels = updatedDaysOffInputs.map((dayOff: any, index: number) => ({
          ...dayOff,
          label: `Day off date ${index + 1}`,
        }));
        return updatedDaysOffInputsWithLabels;
      });
      onDayDeletion({id, period: {from: value?.slice(0, 10), to: value?.slice(0, 10)}})
    };

    return (
      <Box>
        <SectionHeader label="Set the rule" />
        <Box>
          {daysOffInputs.map(({ id, label, value }: any, index: number) => {
            return (
              <Box
                key={id}
                sx={{
                  display: "flex",
                }}>
                <DefaultDateInput
                  initialValue={value}
                  label={label}
                  onChanged={(newValue: any) => setСorrectDate(index, newValue, id, label)}
                />
                <DeleteButton
                  onClick={() => handleDeleteDaysOff(id, value)}
                  disableRipple
                >
                  <TrashBinMenuIconFilled />
                </DeleteButton>
              </Box>
            );
          })}
          <Box
            sx={{
              width: "91.5%"
            }}>
            <DefaultInputSmall
              initialValue={reason}
              onChanged={(newValue) => {
                setReason(newValue);
                onChange(daysOffInputs, newValue)
              }}
              label="Reason"
              placeholder="Enter the reason"
            />
          </Box>
        </Box>
        <Box
          onClick={handleAddMore}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "1px dashed #D6D6DB",
            height: "32px",
            width: "100%",
            color: "#3A3A57",
            borderRadius: "6px",
            cursor: "pointer",
            userSelect: "none",
            marginTop: "16px",

            ":hover": {
              borderColor: "#B9B9B9",
              backgroundColor: "#FAFAFA",
            },
          }}
        >
          <PlusIcon fontSize="inherit" />
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: "400",
              marginLeft: "8px",
              color: "#3A3A57",
            }}
          >
            Add one more day
          </Typography>
        </Box>
      </Box>
    );
  }
);
